import { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Play, Activity, Settings, Copy, CheckCircle, AlertTriangle, Loader2, Save, FolderOpen, Search, RefreshCw } from 'lucide-react';
import { Card, CardHeader, InfoTip } from '../components/ui';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import * as api from '../services/api';

const TABS = ['Builder', 'My Strategies', 'Templates', 'Backtesting'];

// Core indicators the backtest engine actually computes from real OHLCV.
// Derived deltas (X_VS_Y) let you write "EMA9 crossed above EMA20" as
// `EMA9_VS_EMA20 crosses_above 0`, and "price broke the lower Bollinger band"
// as `PRICE_VS_BB_LOWER < 0`. crosses_above/below on a delta indicator fires
// exactly on the crossover candle — perfect for trend-initiation triggers.
const CONDITION_INDICATORS = [
  'RSI', 'EMA9', 'EMA20', 'EMA50', 'BOLLINGER_WIDTH', 'VOLUME', 'PRICE',
  'EMA9_VS_EMA20', 'EMA20_VS_EMA50', 'EMA9_VS_EMA50',
  'PRICE_VS_EMA9', 'PRICE_VS_EMA20', 'PRICE_VS_EMA50',
  'PRICE_VS_BB_UPPER', 'PRICE_VS_BB_LOWER',
];
const OPERATORS = ['>', '<', '>=', '<=', 'crosses_above', 'crosses_below'];
const ACTION_TYPES = ['Market Buy', 'DCA Buy', 'Take Profit', 'Stop Loss', 'Trailing TP', 'Trailing SL', 'Send Alert'];
const LOGIC_TYPES = ['AND', 'OR'];
const TIMEFRAMES = ['5m', '15m', '30m', '1h', '4h', '1d'];
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BTC/USD', 'ETH/USD', 'SOL/USD'];

// Map backend snake_case action types to display labels (and back).
const ACTION_TO_LABEL = {
  market_buy: 'Market Buy',
  dca_buy: 'DCA Buy',
  take_profit: 'Take Profit',
  stop_loss: 'Stop Loss',
  trailing_tp: 'Trailing TP',
  trailing_sl: 'Trailing SL',
  send_alert: 'Send Alert',
};
const LABEL_TO_ACTION = Object.fromEntries(Object.entries(ACTION_TO_LABEL).map(([k, v]) => [v, k]));

const RISK_COLORS = { Low: 'bg-emerald-500/20 text-emerald-400', Medium: 'bg-amber-500/20 text-amber-400', High: 'bg-red-500/20 text-red-400' };

// Timeframe → strategy-type guidance. Educational, not advice. Reminds users
// which trading style each timeframe suits + links to the matching deep-dive
// in the Learning Library. Updates live as the user changes the timeframe pill.
const TIMEFRAME_GUIDANCE = {
  '5m':  { style: 'Scalping',       guideId: 'scalping-trading-explained',  color: 'red',     note: 'Fast in/out trades. Fee drag is brutal at 40 bps — most retail traders should NOT start here.' },
  '15m': { style: 'Day Trading',    guideId: 'day-trading-explained',       color: 'amber',   note: 'Intraday positions. Close all trades within a single session. Requires daily time commitment.' },
  '30m': { style: 'Day Trading',    guideId: 'day-trading-explained',       color: 'amber',   note: 'Intraday positions. Slightly slower than 15m. Same time-commitment expectations.' },
  '1h':  { style: 'Swing Trading',  guideId: 'swing-trading-explained',     color: 'emerald', note: 'Sweet spot for most retail traders. Hold days to weeks. Pair with the Conditional Bot for regime filtering.' },
  '4h':  { style: 'Swing Trading',  guideId: 'swing-trading-explained',     color: 'emerald', note: 'Slower swing trades. Fewer but higher-conviction signals. Hold up to several weeks.' },
  '1d':  { style: 'Position Trading', guideId: 'position-trading-explained', color: 'blue',    note: 'Multi-week to multi-month holds. Low time commitment. Backtest with 1+ year of data for meaningful stats.' },
};
const TF_COLOR_CLASSES = {
  red:     'bg-red-500/5 border-red-500/20 text-red-300',
  amber:   'bg-amber-500/5 border-amber-500/20 text-amber-300',
  emerald: 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300',
  blue:    'bg-blue-500/5 border-blue-500/20 text-blue-300',
};

function sortPairs(symbols) {
  const stables = new Set(['USDT', 'USDC', 'USD', 'BUSD', 'DAI', 'TUSD', 'USDD']);
  return [...symbols].sort((a, b) => {
    const [, qa = ''] = a.split('/');
    const [, qb = ''] = b.split('/');
    const sa = stables.has(qa), sb = stables.has(qb);
    if (sa && !sb) return -1;
    if (!sa && sb) return 1;
    return a.localeCompare(b);
  });
}

export default function StrategyBuilderPage() {
  const [activeTab, setActiveTab] = useState('Builder');

  // ─── Builder state ───
  const [currentStrategyId, setCurrentStrategyId] = useState(null);
  const [strategyName, setStrategyName] = useState('My Strategy');
  const [description, setDescription] = useState('');
  const [risk, setRisk] = useState('Medium');
  const [category, setCategory] = useState('Custom');
  const [conditions, setConditions] = useState([{ id: 1, indicator: 'RSI', operator: '<', value: 35 }]);
  const [logic, setLogic] = useState('AND');
  const [actions, setActions] = useState([
    { id: 1, type: 'Market Buy', size: 10, trail: false },
    { id: 2, type: 'Take Profit', size: 4, trail: false },
    { id: 3, type: 'Stop Loss', size: 2, trail: false },
  ]);
  const [riskConfig, setRiskConfig] = useState({ maxTradesPerDay: 3, maxCapitalPct: 20, dailyLossLimit: 5, drawdownLimit: 10 });

  // ─── Save state ───
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveError, setSaveError] = useState('');

  // ─── My Strategies state ───
  const [myStrategies, setMyStrategies] = useState([]);
  const [mySLoading, setMySLoading] = useState(false);
  const [mySError, setMySError] = useState('');

  // ─── Templates state ───
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // ─── Backtest config ───
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);
  const [btStrategyId, setBtStrategyId] = useState('');
  const [btExchange, setBtExchange] = useState('');
  const [btSymbol, setBtSymbol] = useState('BTC/USDT');
  const [btTimeframe, setBtTimeframe] = useState('1h');
  const [btDays, setBtDays] = useState(90);
  const [btCapital, setBtCapital] = useState(10000);
  const [btFeeBps, setBtFeeBps] = useState(40);
  const [btRunning, setBtRunning] = useState(false);
  const [btResults, setBtResults] = useState(null);
  const [btError, setBtError] = useState('');
  const [btHistory, setBtHistory] = useState([]);

  // Per-exchange pair list
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  // ─── Load user's saved strategies on mount + when My Strategies tab is opened ───
  const loadMyStrategies = async () => {
    setMySLoading(true);
    setMySError('');
    try {
      const data = await api.listStrategies();
      setMyStrategies(data?.strategies || []);
    } catch (err) {
      setMySError(err?.message || 'Failed to load strategies.');
    } finally {
      setMySLoading(false);
    }
  };
  useEffect(() => { loadMyStrategies(); }, []);
  useEffect(() => { if (activeTab === 'My Strategies') loadMyStrategies(); }, [activeTab]);

  // ─── Load templates when Templates tab opens ───
  useEffect(() => {
    if (activeTab !== 'Templates') return;
    setTemplatesLoading(true);
    api.getStrategyTemplates()
      .then(data => setTemplates(data?.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [activeTab]);

  // ─── Load connected exchanges when Backtesting tab opens ───
  useEffect(() => {
    if (activeTab !== 'Backtesting') return;
    setLoadingExchanges(true);
    api.getExchangeConnections()
      .then(res => {
        const list = (res?.exchanges || res?.data?.exchanges || []).filter(e => e.connected);
        setConnectedExchanges(list);
        if (list.length && !btExchange) setBtExchange(list[0].exchangeId);
      })
      .catch(() => setConnectedExchanges([]))
      .finally(() => setLoadingExchanges(false));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Load pairs for chosen exchange ───
  useEffect(() => {
    if (!btExchange) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    api.getExchangeMarkets(btExchange)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || res?.data?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(btSymbol)) setBtSymbol(sorted[0]);
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
  }, [btExchange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter the full per-exchange pair list against the search input.
  // No artificial cap on search results — users searching for a specific
  // pair want every match shown, not the first 100. Empty-search shows
  // the first 200 (sorted with popular pairs first) for browsing.
  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase()))
    : pairs.slice(0, 200);

  // ─── Backtest history for the selected strategy ───
  useEffect(() => {
    if (!btStrategyId) { setBtHistory([]); return; }
    api.listStrategyBacktests(btStrategyId)
      .then(d => setBtHistory(d?.backtests || []))
      .catch(() => setBtHistory([]));
  }, [btStrategyId]);

  // ─── Condition + Action editors ───
  const addCondition = () => setConditions(c => [...c, { id: Date.now(), indicator: 'EMA9', operator: '>', value: 0 }]);
  const removeCondition = id => setConditions(c => c.filter(x => x.id !== id));
  const updateCondition = (id, field, value) => setConditions(c => c.map(x => x.id === id ? { ...x, [field]: value } : x));

  const addAction = () => setActions(a => [...a, { id: Date.now(), type: 'Take Profit', size: 3, trail: false }]);
  const removeAction = id => setActions(a => a.filter(x => x.id !== id));
  const updateAction = (id, field, value) => setActions(a => a.map(x => x.id === id ? { ...x, [field]: value } : x));

  // ─── Build backend payload ───
  const buildPayload = () => ({
    name: strategyName,
    description,
    category,
    risk,
    conditions: {
      type: logic,
      conditions: conditions.map(c => ({ indicator: c.indicator, operator: c.operator, value: parseFloat(c.value) })),
    },
    actions: actions.map(a => ({
      type: LABEL_TO_ACTION[a.type] || 'market_buy',
      size: a.size,
      trail: !!a.trail,
    })),
    riskControls: {
      maxTradesPerDay: riskConfig.maxTradesPerDay,
      maxCapitalPct: riskConfig.maxCapitalPct,
      dailyLossLimit: riskConfig.dailyLossLimit,
      drawdownLimit: riskConfig.drawdownLimit,
    },
  });

  // ─── Save or update ───
  const saveStrategy = async () => {
    if (!strategyName?.trim()) {
      setSaveStatus('error');
      setSaveError('Strategy name is required.');
      setTimeout(() => setSaveStatus(null), 4000);
      return;
    }
    setSaving(true);
    setSaveStatus(null);
    setSaveError('');
    try {
      const payload = buildPayload();
      if (currentStrategyId) {
        await api.updateStrategy(currentStrategyId, payload);
      } else {
        const res = await api.createStrategy(payload);
        if (res?.strategy?.strategyId) setCurrentStrategyId(res.strategy.strategyId);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
      loadMyStrategies();
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err?.message || 'Save failed.');
      setTimeout(() => setSaveStatus(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // ─── Load a saved strategy into Builder ───
  const loadStrategyIntoBuilder = (s) => {
    setCurrentStrategyId(s.strategyId);
    setStrategyName(s.name || '');
    setDescription(s.description || '');
    setCategory(s.category || 'Custom');
    setRisk(s.risk || 'Medium');
    setLogic(s.conditions?.type || 'AND');
    const condList = (s.conditions?.conditions || []).map((c, i) => ({
      id: Date.now() + i,
      indicator: c.indicator || 'RSI',
      operator: c.operator || '>',
      value: c.value ?? 0,
    }));
    setConditions(condList.length ? condList : [{ id: 1, indicator: 'RSI', operator: '<', value: 35 }]);
    const actionList = (s.actions || []).map((a, i) => ({
      id: Date.now() + 100 + i,
      type: ACTION_TO_LABEL[a.type] || a.type || 'Market Buy',
      size: a.size ?? 5,
      trail: !!a.trail,
    }));
    setActions(actionList.length ? actionList : [{ id: 1, type: 'Market Buy', size: 10, trail: false }]);
    setRiskConfig({
      maxTradesPerDay: s.riskControls?.maxTradesPerDay ?? 3,
      maxCapitalPct: s.riskControls?.maxCapitalPct ?? 20,
      dailyLossLimit: s.riskControls?.dailyLossLimit ?? 5,
      drawdownLimit: s.riskControls?.drawdownLimit ?? 10,
    });
    setActiveTab('Builder');
  };

  // ─── Load full strategy from index entry (need details) ───
  const editStrategy = async (strategyId) => {
    try {
      const res = await api.getStrategy(strategyId);
      if (res?.strategy) loadStrategyIntoBuilder(res.strategy);
    } catch (err) {
      setMySError(err?.message || 'Failed to load strategy.');
    }
  };

  // ─── Delete saved strategy ───
  const deleteSaved = async (strategyId) => {
    if (!window.confirm('Delete this strategy? Backtests linked to it will remain in your history.')) return;
    try {
      await api.deleteStrategy(strategyId);
      if (currentStrategyId === strategyId) {
        setCurrentStrategyId(null);
      }
      loadMyStrategies();
    } catch (err) {
      setMySError(err?.message || 'Delete failed.');
    }
  };

  // ─── New strategy (clears builder) ───
  const newStrategy = () => {
    setCurrentStrategyId(null);
    setStrategyName('My Strategy');
    setDescription('');
    setCategory('Custom');
    setRisk('Medium');
    setConditions([{ id: Date.now(), indicator: 'RSI', operator: '<', value: 35 }]);
    setActions([
      { id: Date.now() + 1, type: 'Market Buy', size: 10, trail: false },
      { id: Date.now() + 2, type: 'Take Profit', size: 4, trail: false },
      { id: Date.now() + 3, type: 'Stop Loss', size: 2, trail: false },
    ]);
    setLogic('AND');
    setRiskConfig({ maxTradesPerDay: 3, maxCapitalPct: 20, dailyLossLimit: 5, drawdownLimit: 10 });
    setActiveTab('Builder');
  };

  // ─── Apply starter template (does NOT create or assign an ID — user must Save) ───
  const applyTemplate = (tpl) => {
    setCurrentStrategyId(null);
    setStrategyName(tpl.name || 'Strategy from template');
    setDescription(tpl.description || '');
    setCategory(tpl.category || 'Custom');
    setRisk(tpl.risk || 'Medium');
    setLogic(tpl.conditions?.type || 'AND');
    const condList = (tpl.conditions?.conditions || []).map((c, i) => ({
      id: Date.now() + i,
      indicator: c.indicator,
      operator: c.operator,
      value: c.value,
    }));
    setConditions(condList.length ? condList : [{ id: 1, indicator: 'RSI', operator: '<', value: 35 }]);
    const actionList = (tpl.actions || []).map((a, i) => ({
      id: Date.now() + 100 + i,
      type: ACTION_TO_LABEL[a.type] || a.type || 'Market Buy',
      size: a.size > 1 ? a.size : Math.round((a.size || 0.05) * 100),
      trail: !!a.trail,
    }));
    setActions(actionList.length ? actionList : [{ id: 1, type: 'Market Buy', size: 10, trail: false }]);
    setRiskConfig({
      maxTradesPerDay: tpl.riskControls?.maxTradesPerDay ?? 3,
      maxCapitalPct: tpl.riskControls?.maxCapitalPct ?? 20,
      dailyLossLimit: tpl.riskControls?.maxDailyLossPercent ?? tpl.riskControls?.dailyLossLimit ?? 5,
      drawdownLimit: tpl.riskControls?.drawdownLimit ?? 10,
    });
    setActiveTab('Builder');
  };

  // ─── Run backtest ───
  const runBacktest = async () => {
    if (!btStrategyId) { setBtError('Select a saved strategy to backtest.'); return; }
    if (!btExchange) { setBtError('Connect an exchange to run a backtest.'); return; }
    if (!btSymbol) { setBtError('Select a trading pair.'); return; }
    setBtRunning(true);
    setBtError('');
    setBtResults(null);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const startDate = new Date(Date.now() - btDays * 86400000).toISOString().slice(0, 10);
      const res = await api.runStrategyBacktest(btStrategyId, {
        symbol: btSymbol,
        exchangeId: btExchange,
        timeframe: btTimeframe,
        startDate,
        endDate,
        initialCapital: btCapital,
        feeBps: btFeeBps,
      });
      const results = res?.results || res;
      if (!results) throw new Error('No results returned.');
      setBtResults(results);
      // Refresh history list
      api.listStrategyBacktests(btStrategyId)
        .then(d => setBtHistory(d?.backtests || []))
        .catch(() => {});
    } catch (err) {
      setBtError(err?.message || 'Backtest failed.');
    } finally {
      setBtRunning(false);
    }
  };

  const bt = btResults;

  // Format equity curve for chart (downsampled by backend, just transform keys)
  const equityForChart = useMemo(() => {
    if (!bt?.equityCurve?.length) return [];
    return bt.equityCurve.map(p => ({
      date: new Date(p.ts).toLocaleDateString(),
      value: p.equity,
    }));
  }, [bt]);

  // Build monthly returns from equity curve
  const monthlyReturns = useMemo(() => {
    if (!bt?.equityCurve?.length) return [];
    const byMonth = {};
    bt.equityCurve.forEach(p => {
      const key = new Date(p.ts).toISOString().slice(0, 7); // YYYY-MM
      if (!byMonth[key]) byMonth[key] = { first: p.equity, last: p.equity };
      byMonth[key].last = p.equity;
    });
    return Object.entries(byMonth).map(([month, { first, last }]) => ({
      month,
      return: first > 0 ? Number(((last - first) / first * 100).toFixed(2)) : 0,
    }));
  }, [bt]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Strategy Builder</h1>
          <p className="text-gray-400">Design rule-based strategies, save them to your library, and backtest against real historical OHLCV.</p>
        </div>
        <div className="flex bg-dark-700 rounded-lg overflow-hidden">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm transition-colors ${activeTab === tab ? 'bg-accent-primary text-white' : 'text-gray-400 hover:text-white'}`}>{tab}</button>
          ))}
        </div>
      </div>

      {/* ─── BUILDER TAB ─── */}
      {activeTab === 'Builder' && (
        <div className="space-y-6">
          {/* Strategy Name + Save */}
          <Card>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Activity className="w-5 h-5 text-accent-primary flex-shrink-0 hidden sm:block" />
                <input
                  value={strategyName}
                  onChange={e => setStrategyName(e.target.value)}
                  className="input flex-1 text-lg font-semibold"
                  placeholder="Strategy name..."
                  maxLength={120}
                />
                {currentStrategyId && (
                  <button onClick={newStrategy} className="btn-ghost text-sm whitespace-nowrap" title="Clear builder and start a new strategy">
                    + New
                  </button>
                )}
                <button onClick={saveStrategy} disabled={saving} className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap min-w-[150px]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saveStatus === 'saved' ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving…' : saveStatus === 'saved' ? 'Saved!' : (currentStrategyId ? 'Update Strategy' : 'Save Strategy')}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="input text-sm sm:col-span-3"
                  placeholder="Description (optional, shown in your library)"
                  maxLength={500}
                />
                <select value={category} onChange={e => setCategory(e.target.value)} className="input text-sm">
                  {['Custom', 'Scalping', 'Swing', 'Trend', 'Mean Reversion', 'Breakout', 'DCA', 'Momentum'].map(c => <option key={c}>{c}</option>)}
                </select>
                <select value={risk} onChange={e => setRisk(e.target.value)} className="input text-sm">
                  {['Low', 'Medium', 'High'].map(r => <option key={r}>{r}</option>)}
                </select>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {currentStrategyId ? (
                    <span className="text-accent-primary">Editing: {currentStrategyId.slice(0, 8)}…</span>
                  ) : (
                    <span>New strategy (not yet saved)</span>
                  )}
                </div>
              </div>
              {saveStatus === 'error' && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {saveError}
                </div>
              )}
            </div>
          </Card>

          {/* Condition Blocks */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-accent-primary font-semibold flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> Condition Blocks (WHEN)
                <InfoTip side="right">Use delta indicators like EMA9_VS_EMA20 with the crosses_above operator to fire exactly on the crossover candle instead of every candle the state holds.</InfoTip>
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  Logic
                  <InfoTip side="left">AND = every condition true on the same candle (strict, fewer signals). OR = any one triggers (loose, more noise). Most reliable strategies use AND with 2-3 conditions.</InfoTip>
                  :
                </span>
                {LOGIC_TYPES.map(l => (
                  <button key={l} onClick={() => setLogic(l)} className={`px-3 py-1 text-xs rounded-full transition-colors ${logic === l ? 'bg-blue-600 text-white' : 'bg-dark-600 text-gray-400 hover:text-white'}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {conditions.map((cond, i) => (
                <div key={cond.id}>
                  {i > 0 && <div className="text-center text-xs text-blue-400 my-1">{logic}</div>}
                  <div className="flex items-center gap-2 bg-dark-700 rounded-lg p-3">
                    <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
                    <select value={cond.indicator} onChange={e => updateCondition(cond.id, 'indicator', e.target.value)} className="input text-sm py-1 flex-1">
                      {CONDITION_INDICATORS.map(ind => <option key={ind}>{ind}</option>)}
                    </select>
                    <select value={cond.operator} onChange={e => updateCondition(cond.id, 'operator', e.target.value)} className="input text-sm py-1 w-32">
                      {OPERATORS.map(op => <option key={op}>{op}</option>)}
                    </select>
                    <input type="number" value={cond.value} onChange={e => updateCondition(cond.id, 'value', e.target.value)} className="input text-sm py-1 w-24" step="any" />
                    <button onClick={() => removeCondition(cond.id)} className="text-gray-500 hover:text-red-400 p-1" disabled={conditions.length === 1} title={conditions.length === 1 ? 'A strategy needs at least one condition' : 'Remove'}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addCondition} className="mt-3 btn-ghost text-sm flex items-center gap-2 text-blue-400">
              <Plus className="w-4 h-4" /> Add Condition
            </button>
            <p className="mt-3 text-xs text-gray-500">
              Available: RSI · EMA9/EMA20/EMA50 · Bollinger Width · Volume · Price. MACD/ATR/VWAP coming next pass.
            </p>
          </Card>

          {/* Action Blocks */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-accent-primary font-semibold flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Action Blocks (DO)
                <InfoTip side="right">Size is a percent. For Market Buy / DCA Buy it means % of capital allocated. For TP/SL it means % move from entry (TP=4 = +4% target, SL=2 = -2% stop). Trail flips a fixed TP/SL into a trailing one.</InfoTip>
              </h3>
            </div>
            <div className="space-y-2">
              {actions.map((action, i) => (
                <div key={action.id} className="flex items-center gap-2 bg-dark-700 rounded-lg p-3">
                  <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
                  <select value={action.type} onChange={e => updateAction(action.id, 'type', e.target.value)} className="input text-sm py-1 flex-1">
                    {ACTION_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div className="flex items-center gap-1">
                    <input type="number" value={action.size} onChange={e => updateAction(action.id, 'size', +e.target.value)} className="input text-sm py-1 w-20" min="0.1" max="100" step="0.5" />
                    <span className="text-gray-400 text-xs">%</span>
                  </div>
                  {(action.type.includes('TP') || action.type.includes('SL') || action.type.includes('Trailing')) && (
                    <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
                      <input type="checkbox" checked={action.trail} onChange={e => updateAction(action.id, 'trail', e.target.checked)} className="rounded" />
                      Trail
                    </label>
                  )}
                  <button onClick={() => removeAction(action.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={addAction} className="mt-3 btn-ghost text-sm flex items-center gap-2 text-emerald-400">
              <Plus className="w-4 h-4" /> Add Action
            </button>
            <p className="mt-3 text-xs text-gray-500">
              A strategy needs <strong>at least one entry action</strong> (Market Buy or DCA Buy) to backtest. Take Profit / Stop Loss are optional exit legs.
            </p>
          </Card>

          {/* Risk Controls */}
          <Card>
            <h3 className="text-accent-primary font-semibold flex items-center gap-2 mb-4">
              <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" /> Risk Controls
              <InfoTip side="right">Hard stops enforced by the bot engine, not the strategy. They cap downside if conditions misfire live. The backtest also respects them.</InfoTip>
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Max Trades/Day', key: 'maxTradesPerDay', unit: 'trades', min: 1, max: 20, tip: 'Cap on fire frequency. Hit this and the bot waits for the UTC day to roll over.' },
                { label: 'Max Capital %', key: 'maxCapitalPct', unit: '%', min: 1, max: 100, tip: 'Largest single position the strategy can hold. 20 = max 20% of capital in one open trade.' },
                { label: 'Daily Loss Limit', key: 'dailyLossLimit', unit: '%', min: 0.5, max: 20, tip: 'Auto-pause if cumulative losses hit this in a single UTC day. 5 = pause after -5% day.' },
                { label: 'Drawdown Limit', key: 'drawdownLimit', unit: '%', min: 1, max: 50, tip: 'Auto-pause if equity drops this far from its all-time peak. 10 = pause at -10% from high-water mark.' },
              ].map(f => (
                <div key={f.key} className="bg-dark-700 rounded-lg p-3">
                  <label className="text-gray-400 text-xs flex items-center gap-1">
                    {f.label}
                    <InfoTip side="top">{f.tip}</InfoTip>
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number" value={riskConfig[f.key]}
                      onChange={e => setRiskConfig(r => ({ ...r, [f.key]: +e.target.value }))}
                      className="input text-sm py-1 flex-1" min={f.min} max={f.max} step={f.min}
                    />
                    <span className="text-gray-500 text-xs">{f.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Strategy Preview */}
          <Card>
            <h3 className="text-accent-primary font-semibold mb-3 flex items-center gap-2"><Settings className="w-4 h-4" /> Strategy Preview</h3>
            <div className="bg-dark-800 rounded-lg p-4 font-mono text-xs text-gray-300 space-y-1">
              <p><span className="text-accent-primary">WHEN</span> {conditions.map((c, i) => <span key={c.id}>{i > 0 && <span className="text-blue-400"> {logic} </span>}<span className="text-yellow-400">{c.indicator}</span> {c.operator} <span className="text-emerald-400">{c.value}</span></span>)}</p>
              <p><span className="text-accent-primary">DO</span> {actions.map((a, i) => <span key={a.id}>{i > 0 ? ', ' : ''}<span className="text-yellow-400">{a.type}</span> <span className="text-emerald-400">{a.size}%</span></span>)}</p>
              <p><span className="text-accent-primary">RISK</span> Max {riskConfig.maxTradesPerDay} trades/day · Stop if loss &gt; {riskConfig.dailyLossLimit}% · Drawdown limit {riskConfig.drawdownLimit}%</p>
            </div>
          </Card>
        </div>
      )}

      {/* ─── MY STRATEGIES TAB ─── */}
      {activeTab === 'My Strategies' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Your Saved Strategies</h3>
              <p className="text-sm text-gray-400">Strategies you can edit, backtest, or reference from the Conditional Bot.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={loadMyStrategies} className="btn-ghost text-sm flex items-center gap-1" title="Refresh">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button onClick={newStrategy} className="btn-primary text-sm flex items-center gap-1">
                <Plus className="w-4 h-4" /> New Strategy
              </button>
            </div>
          </div>
          {mySError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {mySError}
            </div>
          )}
          {mySLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
            </div>
          ) : myStrategies.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <FolderOpen className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">No saved strategies yet.</p>
                <p className="text-xs text-gray-500 mt-1">Build one in the Builder tab and click Save, or copy a starter from Templates.</p>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myStrategies.map(s => (
                <Card key={s.strategyId} hover className="flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-white truncate flex-1 mr-2">{s.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${RISK_COLORS[s.risk] || RISK_COLORS.Medium}`}>{s.risk || 'Medium'}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{s.category || 'Custom'} · Saved {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'recently'}</p>
                  <div className="flex items-center gap-2 mt-auto pt-3 border-t border-dark-700">
                    <button onClick={() => editStrategy(s.strategyId)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1 flex-1 justify-center">
                      <Settings className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => { setBtStrategyId(s.strategyId); setActiveTab('Backtesting'); }}
                      className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1 flex-1 justify-center text-purple-400"
                    >
                      <Play className="w-3 h-3" /> Backtest
                    </button>
                    <button onClick={() => deleteSaved(s.strategyId)} className="text-gray-500 hover:text-red-400 p-1.5" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── TEMPLATES TAB ─── */}
      {activeTab === 'Templates' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Starter Templates</h3>
            <p className="text-sm text-gray-400">
              Click <span className="text-white font-medium">Use Template</span> to copy a strategy into the Builder.
              Customize the conditions, risk controls, or rename it — then Save to add your version to your library.
              All conditions use indicators the backtest engine actually computes, so every template fires correctly when you backtest it.
            </p>
          </div>
          <div className="flex items-start gap-2.5 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-300/80">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>Templates are starting points based on common technical-analysis techniques. Past strategy logic does not guarantee future performance. Always backtest before deploying, and never risk capital you cannot afford to lose.</span>
          </div>
          {templatesLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <Card>
              <p className="text-sm text-gray-400 text-center py-6">No templates available right now.</p>
            </Card>
          ) : (() => {
            // Group templates by level for tiered display
            const buckets = { Basic: [], Intermediate: [], Advanced: [], Other: [] };
            templates.forEach(t => {
              const lvl = t.level || 'Other';
              if (buckets[lvl]) buckets[lvl].push(t);
              else buckets.Other.push(t);
            });
            const levelMeta = {
              Basic:        { color: 'text-emerald-400', dot: 'bg-emerald-500', subtitle: 'Single-concept entries · easy to read · great for new users learning the system' },
              Intermediate: { color: 'text-amber-400',   dot: 'bg-amber-500',   subtitle: 'Multi-condition filters · structural + momentum confirmation · institutional-grade risk controls' },
              Advanced:     { color: 'text-purple-400', dot: 'bg-purple-500',  subtitle: 'Nested AND/OR trees · multi-factor confluence · regime-aware logic · trailing exits' },
              Other:        { color: 'text-gray-400',   dot: 'bg-gray-500',    subtitle: '' },
            };
            return ['Basic', 'Intermediate', 'Advanced', 'Other'].map(level => {
              const list = buckets[level];
              if (list.length === 0) return null;
              const meta = levelMeta[level];
              return (
                <div key={level} className="space-y-3">
                  <div className="flex items-baseline justify-between border-b border-dark-700 pb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                      <h4 className={`text-base font-semibold ${meta.color}`}>{level}</h4>
                      <span className="text-xs text-gray-500">· {list.length} strategies</span>
                    </div>
                    {meta.subtitle && <span className="text-xs text-gray-500 hidden sm:block">{meta.subtitle}</span>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map(tpl => (
                      <Card key={tpl.id || tpl.name} hover className="flex flex-col">
                        <div className="flex items-start justify-between mb-3 gap-2">
                          <h3 className="font-semibold text-white text-sm leading-tight flex-1">{tpl.name}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${RISK_COLORS[tpl.risk] || RISK_COLORS.Medium}`}>{tpl.risk || 'Medium'}</span>
                        </div>
                        <p className="text-gray-400 text-xs flex-1 mb-3 leading-relaxed">{tpl.description}</p>
                        {(tpl.conditions?.conditions || []).length > 0 && (
                          <div className="bg-dark-700/50 rounded-lg p-2 mb-3 space-y-1 max-h-24 overflow-y-auto">
                            {(tpl.conditions?.conditions || []).map((c, i) => (
                              <p key={i} className="text-[10px] font-mono text-gray-400 truncate">
                                {c.type ? (
                                  <span className="text-blue-400">{c.type}({(c.conditions || []).length} sub-conditions)</span>
                                ) : (
                                  <>
                                    <span className="text-yellow-400">{c.indicator}</span> {c.operator} <span className="text-emerald-400">{c.value}</span>
                                  </>
                                )}
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-auto">
                          <span className="text-[10px] text-gray-500">{tpl.category}</span>
                          <button onClick={() => applyTemplate(tpl)} className="btn-primary text-xs flex items-center gap-1 py-1.5 px-3">
                            <Copy className="w-3 h-3" /> Use Template
                          </button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ─── BACKTESTING TAB ─── */}
      {activeTab === 'Backtesting' && (
        <div className="space-y-6">
          {/* Explanation */}
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="text-blue-400 font-semibold">What is backtesting?</span> The engine fetches real historical OHLCV from your selected exchange, computes RSI / EMA / Bollinger / volume for each candle, and walks forward through your strategy's conditions. Every entry signal opens a position; TP/SL/trailing exits are simulated intra-candle. Fees are charged per fill. Results below are based on actual exchange data — not synthetic prices.
            </p>
            <p className="text-xs text-blue-300/80 mt-2.5">
              📚 New to backtesting? Read{' '}
              <a href="/learning-library?guide=swing-trading-explained" className="underline hover:text-blue-200">Swing Trading</a>{' · '}
              <a href="/learning-library?guide=scalping-trading-explained" className="underline hover:text-blue-200">Scalping</a>{' · '}
              <a href="/learning-library?guide=day-trading-explained" className="underline hover:text-blue-200">Day Trading</a>{' · '}
              <a href="/learning-library?guide=position-trading-explained" className="underline hover:text-blue-200">Position Trading</a>{' · '}
              <a href="/learning-library?guide=indicator-reference" className="underline hover:text-blue-200">Indicator Reference</a>
            </p>
          </div>

          {/* Config */}
          <Card>
            <CardHeader title="Backtest Configuration" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Strategy */}
              <div>
                <label className="text-gray-400 text-sm">Strategy</label>
                {mySLoading ? (
                  <div className="input w-full mt-1 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : myStrategies.length > 0 ? (
                  <select value={btStrategyId} onChange={e => { setBtStrategyId(e.target.value); setBtError(''); }} className="input w-full mt-1 text-sm">
                    <option value="">— Select Strategy —</option>
                    {myStrategies.map(s => <option key={s.strategyId} value={s.strategyId}>{s.name}</option>)}
                  </select>
                ) : (
                  <div className="mt-1 text-sm text-amber-400">
                    No saved strategies. Build one and click Save, or copy a starter from Templates.
                  </div>
                )}
              </div>
              {/* Exchange */}
              <div>
                <label className="text-gray-400 text-sm">Exchange (for historical OHLCV)</label>
                {loadingExchanges ? (
                  <div className="input w-full mt-1 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : connectedExchanges.length > 0 ? (
                  <select value={btExchange} onChange={e => setBtExchange(e.target.value)} className="input w-full mt-1 text-sm">
                    {connectedExchanges.map(e => <option key={e.exchangeId} value={e.exchangeId}>{e.name || e.exchangeId}</option>)}
                  </select>
                ) : (
                  <div className="mt-1 text-sm text-amber-400">
                    No exchange connected. Add one in <a href="/settings?tab=connections" className="underline">Settings</a>.
                  </div>
                )}
              </div>
            </div>

            {/* Symbol search — same pattern as Algo Orders / Grid Bot / Conditional */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-gray-400 text-sm">Trading Pair</label>
                <span className="text-[10px] text-gray-500">
                  {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
                </span>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={pairSearch}
                  onChange={e => setPairSearch(e.target.value)}
                  placeholder={pairsLoading ? 'Loading pairs…' : 'Search pair (e.g. BTC, ETH/USDC)'}
                  className="input w-full text-sm pl-9 mb-2"
                />
              </div>
              <div className="max-h-48 overflow-y-auto bg-dark-700/50 border border-dark-600 rounded-lg">
                {pairsLoading ? (
                  <div className="p-3 text-xs text-gray-500 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {btExchange} markets…
                  </div>
                ) : filteredPairs.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500">No pairs match "{pairSearch}"</div>
                ) : (
                  filteredPairs.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setBtSymbol(p)}
                      className={`block w-full text-left px-3 py-1.5 text-sm font-mono transition-colors ${
                        btSymbol === p
                          ? 'bg-accent-primary/15 text-accent-primary'
                          : 'text-gray-300 hover:bg-dark-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))
                )}
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  Selected: <span className="font-mono text-white">{btSymbol || '(none)'}</span>
                </span>
                {!pairsLoading && (
                  <span className="text-gray-500">
                    {pairSearch
                      ? `${filteredPairs.length} match${filteredPairs.length === 1 ? '' : 'es'}`
                      : `Showing top ${Math.min(200, pairs.length)} — search to find more`}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-gray-400 text-sm">Timeframe</label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {TIMEFRAMES.map(tf => (
                    <button
                      key={tf}
                      onClick={() => setBtTimeframe(tf)}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${btTimeframe === tf ? 'bg-accent-primary text-white' : 'bg-dark-700 text-gray-400 hover:text-white'}`}
                    >{tf}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-sm">Period</label>
                <select value={btDays} onChange={e => setBtDays(+e.target.value)} className="input w-full mt-1 text-sm">
                  <option value={7}>7 Days</option>
                  <option value={30}>30 Days</option>
                  <option value={90}>90 Days</option>
                  <option value={180}>180 Days</option>
                  <option value={365}>1 Year</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm">Initial Capital</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="number" value={btCapital} onChange={e => setBtCapital(+e.target.value)} className="input flex-1 text-sm" min={100} step={100} />
                  <span className="text-gray-400 text-sm">USD</span>
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-sm flex items-center gap-1">
                  Fee per fill
                  <InfoTip side="top">100 bps = 1%. Coinbase taker ~40 bps, Binance US taker ~40 bps, Kraken ~26 bps. Use the realistic taker fee for your tier — scalping strategies die under unrealistic 0 bps assumptions.</InfoTip>
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="number" value={btFeeBps} onChange={e => setBtFeeBps(+e.target.value)} className="input flex-1 text-sm" min={0} max={500} step={5} />
                  <span className="text-gray-400 text-sm">bps</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">{btFeeBps} bps = {(btFeeBps / 100).toFixed(2)}%</p>
              </div>
            </div>

            {/* Timeframe → Strategy Type guidance (educational, not advice) */}
            {TIMEFRAME_GUIDANCE[btTimeframe] && (
              <div className={`mb-4 rounded-xl border px-3 py-2.5 text-xs flex items-start gap-2.5 ${TF_COLOR_CLASSES[TIMEFRAME_GUIDANCE[btTimeframe].color]}`}>
                <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 leading-relaxed">
                  <span className="font-semibold">{btTimeframe} → {TIMEFRAME_GUIDANCE[btTimeframe].style}.</span>{' '}
                  {TIMEFRAME_GUIDANCE[btTimeframe].note}{' '}
                  <a href={`/learning-library?guide=${TIMEFRAME_GUIDANCE[btTimeframe].guideId}`}
                     className="underline hover:opacity-80 whitespace-nowrap">
                    Read the {TIMEFRAME_GUIDANCE[btTimeframe].style} guide →
                  </a>
                </div>
              </div>
            )}

            {/* Anti-patterns — what NOT to do. Compact, scannable, prevents
                common backtest mistakes that make stats look better than
                they really are. */}
            <details className="mb-4 bg-dark-800 border border-dark-600 rounded-xl text-xs">
              <summary className="px-3 py-2 cursor-pointer text-gray-300 hover:text-white flex items-center gap-2 select-none">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="font-semibold">What NOT to do when backtesting</span>
                <span className="text-gray-500 ml-auto">(click to expand)</span>
              </summary>
              <div className="px-3 pb-3 space-y-1.5 text-gray-400 leading-relaxed border-t border-dark-700 pt-2.5">
                <p>❌ <strong className="text-gray-300">Don't pair the wrong timeframe with your strategy.</strong> A swing strategy on 5m gets chopped by noise; a scalping strategy on 4h never fires. Match your strategy's hold time to the timeframe.</p>
                <p>❌ <strong className="text-gray-300">Don't trust Sharpe under 30 closed trades.</strong> Small samples = noisy stats. A Sharpe of -7 on 5 trades is meaningless; same Sharpe on 100 trades is real.</p>
                <p>❌ <strong className="text-gray-300">Don't backtest only the period where it worked.</strong> Test multiple windows (uptrend, range, downtrend). A strategy that profits in trends but loses in chop is fine — IF you gate it with the Conditional Bot.</p>
                <p>❌ <strong className="text-gray-300">Don't underestimate fees.</strong> 40 bps is realistic for Coinbase retail. Set it lower only if you genuinely qualify for maker rebates or zero-fee tiers.</p>
                <p>❌ <strong className="text-gray-300">Don't curve-fit by tweaking until the backtest looks good.</strong> Save the original. Test in a different period (out-of-sample). If it only works on the tuned period, you've fit noise, not edge.</p>
                <p className="pt-1 text-gray-500">
                  See the full <a href="/learning-library?guide=indicator-reference" className="text-amber-400 underline">Indicator Reference</a> and{' '}
                  <a href="/learning-library?guide=swing-trading-explained" className="text-amber-400 underline">Strategy Foundations</a> guides.
                </p>
              </div>
            </details>

            <button onClick={runBacktest} disabled={btRunning || !btStrategyId || !btExchange} className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {btRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {btRunning ? 'Running backtest…' : 'Run Backtest'}
            </button>
            {btError && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {btError}
              </div>
            )}
          </Card>

          {/* Results */}
          {bt && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Return', value: bt.totalReturnPct != null ? `${bt.totalReturnPct >= 0 ? '+' : ''}${bt.totalReturnPct}%` : '—', color: (bt.totalReturnPct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400' },
                  { label: 'Win Rate', value: bt.winRate != null ? `${(bt.winRate * 100).toFixed(1)}%` : '—', color: 'text-accent-primary' },
                  { label: 'Max Drawdown', value: bt.maxDrawdownPct != null ? `${bt.maxDrawdownPct}%` : '—', color: 'text-red-400' },
                  { label: 'Sharpe (annualized)', value: bt.sharpeRatio != null ? bt.sharpeRatio : '—', color: 'text-chart-purple' },
                ].map(stat => (
                  <Card key={stat.label}>
                    <p className="text-gray-400 text-xs">{stat.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
                    {stat.label === 'Total Return' && bt.finalEquity != null && <p className="text-xs text-gray-500 mt-1">${bt.finalEquity.toLocaleString()} from ${bt.initialCapital.toLocaleString()}</p>}
                    {stat.label === 'Win Rate' && bt.totalTrades != null && <p className="text-xs text-gray-500 mt-1">{bt.totalTrades} closed trades</p>}
                  </Card>
                ))}
              </div>

              {/* Profit factor + avg win/loss */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <p className="text-gray-400 text-xs">Profit Factor</p>
                  <p className="text-xl font-semibold text-white mt-1">{bt.profitFactor ?? '—'}</p>
                  <p className="text-[10px] text-gray-500 mt-1">Gross wins ÷ gross losses</p>
                </Card>
                <Card>
                  <p className="text-gray-400 text-xs">Avg Win / Loss</p>
                  <p className="text-xl font-semibold mt-1">
                    <span className="text-emerald-400">${bt.avgWin ?? 0}</span> <span className="text-gray-500">/</span> <span className="text-red-400">${bt.avgLoss ?? 0}</span>
                  </p>
                </Card>
                <Card>
                  <p className="text-gray-400 text-xs">Candles Used</p>
                  <p className="text-xl font-semibold text-white mt-1">{bt.candlesUsed?.toLocaleString() ?? '—'}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{bt.period?.timeframe || ''} · {bt.period?.startDate} → {bt.period?.endDate}</p>
                </Card>
                <Card>
                  <p className="text-gray-400 text-xs">Fee Drag</p>
                  <p className="text-xl font-semibold text-white mt-1">{bt.feeBps ?? '—'} bps</p>
                  <p className="text-[10px] text-gray-500 mt-1">Charged on each fill</p>
                </Card>
              </div>

              {/* Equity Curve */}
              {equityForChart.length > 1 && (
                <Card>
                  <CardHeader title="Equity Curve" subtitle={`$${bt.initialCapital?.toLocaleString()} → $${bt.finalEquity?.toLocaleString()}`} />
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={equityForChart}>
                      <defs>
                        <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} interval={Math.max(1, Math.floor(equityForChart.length / 6))} />
                      <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => `$${v.toLocaleString()}`} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #374151', borderRadius: '8px' }} formatter={v => [`$${Number(v).toLocaleString()}`, 'Equity']} />
                      <Area type="monotone" dataKey="value" stroke="#22c55e" fill="url(#eqGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Monthly Returns */}
              {monthlyReturns.length > 0 && (
                <Card>
                  <CardHeader title="Monthly Returns %" />
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={monthlyReturns}>
                      <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} unit="%" />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #374151', borderRadius: '8px' }} formatter={v => [`${v}%`, 'Return']} />
                      <Bar dataKey="return" radius={[3, 3, 0, 0]}>
                        {monthlyReturns.map((entry, i) => (
                          <Cell key={i} fill={entry.return >= 0 ? '#22c55e' : '#f87171'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Recent trades */}
              {bt.trades?.length > 0 && (
                <Card>
                  <CardHeader title={`Recent Trades (last ${bt.trades.length} of ${bt.tradeCount ?? bt.trades.length})`} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 border-b border-dark-700">
                        <tr>
                          <th className="text-left py-2 px-2 font-medium">Time</th>
                          <th className="text-left py-2 px-2 font-medium">Side</th>
                          <th className="text-right py-2 px-2 font-medium">Price</th>
                          <th className="text-right py-2 px-2 font-medium">Qty</th>
                          <th className="text-right py-2 px-2 font-medium">P&L</th>
                          <th className="text-left py-2 px-2 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bt.trades.slice(-25).reverse().map((t, i) => (
                          <tr key={i} className="border-b border-dark-700/50">
                            <td className="py-2 px-2 text-gray-400">{new Date(t.ts).toLocaleString()}</td>
                            <td className={`py-2 px-2 font-medium ${t.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>{t.side?.toUpperCase()}</td>
                            <td className="py-2 px-2 text-right text-gray-300">${t.price}</td>
                            <td className="py-2 px-2 text-right text-gray-300">{t.qty}</td>
                            <td className={`py-2 px-2 text-right font-medium ${t.pnlUsd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {t.pnlUsd != null ? `${t.pnlUsd >= 0 ? '+' : ''}$${t.pnlUsd}` : '—'}
                            </td>
                            <td className="py-2 px-2 text-gray-500">{t.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {bt.totalTrades === 0 && (
                <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl text-center">
                  <p className="text-amber-400 text-sm">No trades triggered during this period. Try loosening your entry conditions or selecting a longer / more volatile period.</p>
                </div>
              )}
            </>
          )}

          {/* Backtest history for this strategy */}
          {btStrategyId && btHistory.length > 0 && (
            <Card>
              <CardHeader title="Previous Backtest Runs" subtitle={`${btHistory.length} run${btHistory.length === 1 ? '' : 's'} on this strategy`} />
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 border-b border-dark-700">
                    <tr>
                      <th className="text-left py-2 px-2 font-medium">When</th>
                      <th className="text-left py-2 px-2 font-medium">Exchange</th>
                      <th className="text-left py-2 px-2 font-medium">Pair</th>
                      <th className="text-left py-2 px-2 font-medium">TF</th>
                      <th className="text-right py-2 px-2 font-medium">Return</th>
                      <th className="text-right py-2 px-2 font-medium">Win %</th>
                      <th className="text-right py-2 px-2 font-medium">Sharpe</th>
                      <th className="text-right py-2 px-2 font-medium">Trades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {btHistory.slice(0, 20).map(h => (
                      <tr key={h.backtestId} className="border-b border-dark-700/50">
                        <td className="py-2 px-2 text-gray-400">{new Date(h.runAt).toLocaleString()}</td>
                        <td className="py-2 px-2 text-gray-300">{h.exchangeId}</td>
                        <td className="py-2 px-2 text-gray-300">{h.symbol}</td>
                        <td className="py-2 px-2 text-gray-400">{h.timeframe}</td>
                        <td className={`py-2 px-2 text-right font-medium ${(h.totalReturnPct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {h.totalReturnPct != null ? `${h.totalReturnPct >= 0 ? '+' : ''}${h.totalReturnPct}%` : '—'}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-300">{h.winRate != null ? `${(h.winRate * 100).toFixed(1)}%` : '—'}</td>
                        <td className="py-2 px-2 text-right text-gray-300">{h.sharpeRatio ?? '—'}</td>
                        <td className="py-2 px-2 text-right text-gray-300">{h.totalTrades ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
